import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Award, ArrowRight } from "lucide-react";
import { openCertificatePdf } from "@/lib/certificateDownload";

const CARD = {
    hidden: { opacity: 0, y: 12 },
    show: {
        opacity: 1,
        y: 0,
        transition: { type: "spring", stiffness: 320, damping: 30 },
    },
};

/**
 * ProgramCard — one enrollment tile in the My Programs grid.
 * Features a shimmer effect on the progress bar + subtle hover lift.
 */
export default function ProgramCard({ enrollment }) {
    const e = enrollment;
    const pct = e.progress_percent || 0;

    return (
        <motion.article
            variants={CARD}
            whileHover={{ y: -3 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="group relative rounded-2xl bg-card border border-primary/10 overflow-hidden flex flex-col hover:shadow-xl hover:shadow-primary/5 hover:border-primary/15 transition-all"
            data-testid={`program-card-${e.program_id}`}
        >
            {/* Thumbnail */}
            <div className="relative aspect-[5/3] overflow-hidden bg-muted">
                {e.program?.thumbnail_url ? (
                    <img
                        src={e.program.thumbnail_url}
                        alt=""
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                    />
                ) : (
                    <div className="w-full h-full bg-primary/5" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-primary/40 via-transparent to-transparent opacity-60" />
                {e.completed_at && (
                    <div className="absolute top-3 right-3">
                        <Badge className="bg-accent text-accent-foreground gap-1 shadow-lg">
                            <Award className="h-3 w-3" />
                            Completed
                        </Badge>
                    </div>
                )}
            </div>

            {/* Body */}
            <div className="p-5 flex-1 flex flex-col">
                <div className="flex items-center gap-2 mb-2">
                    {e.program?.subject && (
                        <Badge
                            variant="outline"
                            className="capitalize text-[10px] font-normal"
                        >
                            {e.program.subject}
                        </Badge>
                    )}
                </div>
                <h3 className="font-display text-lg text-primary leading-snug line-clamp-2">
                    {e.program?.title}
                </h3>

                {/* Progress */}
                <div className="mt-4">
                    <div className="flex items-center justify-between text-[11px] text-muted-foreground mb-1.5">
                        <span>
                            {e.chapters_completed} / {e.total_chapters} chapters
                        </span>
                        <span className="font-medium text-primary tabular-nums">
                            {pct}%
                        </span>
                    </div>
                    <div className="h-2 rounded-full bg-primary/5 overflow-hidden relative">
                        <div
                            className="h-full rounded-full shimmer-bar"
                            style={{
                                width: `${pct}%`,
                                background:
                                    "linear-gradient(90deg, #D4A140 0%, #E8C07A 50%, #D4A140 100%)",
                                backgroundSize: "200% 100%",
                            }}
                        />
                    </div>
                </div>

                {/* Actions */}
                <div className="mt-5 flex gap-2">
                    <Button
                        asChild
                        className="bg-primary hover:bg-primary/90 flex-1 gap-1.5"
                        size="sm"
                        data-testid={`program-continue-button-${e.program_id}`}
                    >
                        <Link to={`/learn/${e.program_id}`}>
                            {e.completed_at ? "Review" : "Continue"}
                            <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                    </Button>
                    {e.certificate_id && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openCertificatePdf(e.certificate_id)}
                            className="border-accent/40 text-accent hover:bg-accent/10 hover:text-accent gap-1.5"
                            data-testid={`program-certificate-download-${e.program_id}`}
                            title="Download certificate"
                        >
                            <Award className="h-3.5 w-3.5" />
                        </Button>
                    )}
                </div>
            </div>
        </motion.article>
    );
}
